/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-control-regex */

import React, { useState } from 'react';
import styled from 'styled-components';
import 'url-search-params-polyfill';

import { object as YupObject, string as YupString } from 'yup';
import { Formik } from 'formik';

import Input from '@ntuctech/devex-tangram/Input';
import Box from '@ntuctech/devex-tangram/Box';
import Button from '@ntuctech/devex-tangram/Button';
import Link from '@ntuctech/devex-tangram/Link';

import InputValidator from '../../services/input-validator';
import GA from '../../services/google-analytics-tags';

import { useAuth0 } from '../Auth0Provider';
import Error from '../theme/Error';
import Logger from '../../services/logger';
import sentry from '../../services/sentry';

import FormElement from '../theme/FormElement';
import FormActions from '../theme/FormActions';

import { usePageContext } from '../PageContextProvider';
import { PAGE_CONTEXT } from '../../constants/app-constants';

const { captureException } = sentry();

const MobileResolverSC = styled('div')`
  @media (max-width: ${(props) => `${parseInt(props.theme.SizeBreakpointsS, 10) - 1}px`}) {
    & .SSOForgotLink {
      margin-top: -0.75rem;
    }
  }
`;

function FormContent(props) {
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    isValid,
    dirty,
    isSubmitting,
    submitForm,
  } = props;

  const { email, password } = values;

  const showErrorFor = (name) => {
    return touched[name] && Boolean(errors[name]);
  };
  const showEmailError = showErrorFor('email');
  const showPasswordError = showErrorFor('password');

  const isSubmitButtonDisabled = !dirty || !isValid || isSubmitting;

  const { setPageContext } = usePageContext();
  const handleForgot = () => {
    GA.track('Sign-in', 'Navigate to Forgot Password');
    setPageContext(PAGE_CONTEXT.FORGOT);
  };

  const handleFocus = React.useCallback((event) => {
    GA.track('Sign-in', `Sign-in Interacted`, event.target.name);
  }, []);

  const handleKeyPress = React.useCallback(
    (event) => {
      if (event.key === 'Enter' && !isSubmitButtonDisabled) {
        event.target.blur();
        submitForm();
      }
    },
    [submitForm, isSubmitButtonDisabled]
  );

  return (
    <>
      <FormElement>
        <Input
          id="IdentityLoginEmail"
          type="email"
          name="email"
          label="Email address"
          helpText={showEmailError ? errors.email : ''}
          error={showEmailError}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          value={email}
          required
          fullWidth
          autoComplete="new-email"
          onKeyPress={handleKeyPress}
          data-testid="loginUserName"
        />
      </FormElement>
      <FormElement>
        <Input
          id="IdentityLoginPassword"
          type="password"
          name="password"
          label="Password"
          helpText={showPasswordError ? errors.password : ''}
          error={showPasswordError}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          value={password}
          required
          fullWidth
          autoComplete="current-password"
          onKeyPress={handleKeyPress}
          data-testid="loginPassword"
        />
      </FormElement>

      <MobileResolverSC>
        <Box className="SSOForgotLink" mt={-2}>
          <Link onClick={handleForgot} size="m" mode="light" href="#" data-testid="forgotPassword">
            Forgot Password
          </Link>
        </Box>

        <Box className="SSOLoginBtn" mt={-2} mb={-5}>
          <FormActions>
            <Button
              variant="contained"
              fullWidth
              compact
              disabled={isSubmitButtonDisabled}
              onClick={submitForm}
              data-testid="logIn"
            >
              Log in
            </Button>
          </FormActions>
        </Box>
      </MobileResolverSC>
    </>
  );
}

export default function LoginForm() {
  const Auth0Instance = useAuth0();

  const [error, setError] = useState(null);
  const [prefillEmail, setPrefillEmail] = useState('');

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('handoff_email')) {
      const decodedEmail = window.atob(urlParams.get('handoff_email'));
      setPrefillEmail(decodedEmail);
    }
  }, []);

  const LoginSchema = YupObject().shape({
    email: InputValidator.emailValidation({
      emptyEmailMessage: 'Email address is required.',
      invalidEmailMessage: 'Email address is not valid.',
      maxLengthMessage: 'Your email address should be within 64 characters.',
    }),
    password: YupString().trim().required('Password is required.'),
  });

  const submit2Auth0 = (values, actions) => {
    GA.track('Sign-in', 'Sign-in Initiated');

    Auth0Instance.login(
      {
        ...values,
      },
      (err) => {
        actions.setSubmitting(false);
        setError(err);
        if (err) {
          GA.track('Sign-in', 'Sign-in Failure');
          Logger.error(err);
          return;
        }
        GA.track('Sign-in', 'Sign-in Success');
      }
    );
    return false;
  };

  const onSubmit = (values, actions) => {
    if (
      window.PasswordCredential &&
      window.PublicKeyCredential &&
      typeof PublicKeyCredential !== 'undefined'
    ) {
      const { email, password } = values;
      // eslint-disable-next-line no-undef
      const c = new PasswordCredential({ id: email, password });
      navigator.credentials
        .store(c)
        .catch((err) => {
          captureException(err);
          Logger.error(err);
        })
        .finally(() => {
          submit2Auth0(values, actions);
        });
    } else {
      submit2Auth0(values, actions);
    }
  };

  return (
    <>
      {!error ? null : <Error error={error} />}
      <Formik
        initialValues={{ email: prefillEmail, password: '' }}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={LoginSchema}
      >
        {(formikProps) => <FormContent {...formikProps} />}
      </Formik>
    </>
  );
}
